import PropTypes from 'prop-types';
import { Box, Typography, Avatar } from '@mui/material';
import StatusPill from 'features/IRWorkstation/components/Common/StatusPill';
import KPICard from 'features/IRWorkstation/components/Common/KPICard';
import fakeData from 'features/IRWorkstation/constants/fakeData';

const LPPanel = ({ open, lp, onClose }) => {
	if (!lp || !open) return null;

	const healthColor =
		lp.health === 'Strong'
			? 'green'
			: lp.health === 'Stable'
				? 'teal'
				: lp.health === 'At Risk'
					? 'red'
					: 'gray';

	const reupColor =
		lp.reup === 'High' ? 'green' : lp.reup === 'Medium' ? 'amber' : 'gray';

	const hasSessions =
		lp.vdrStatus !== 'No access' &&
		lp.vdrStatus !== 'Not in cohort' &&
		lp.vdrStatus !== 'No engagement';

	const nextAction =
		lp.health === 'At Risk'
			? `Initiate re-engagement — no meaningful contact in ${lp.lastContact}`
			: lp.reup === 'Unknown'
				? 'Identify new primary contact — email bounce detected'
				: lp.reup === 'Medium'
					? 'Schedule DPI-focused bilateral call within 10 days'
					: 'Follow up on VDR engagement — recent session activity suggests IC preparation';

	return (
		<Box
			sx={{
				position: 'absolute',
				inset: 0,
				zIndex: 100,
				backgroundColor: 'background.default',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}
		>
			{/* header bar */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '12px',
					padding: '0 18px',
					height: 48,
					borderBottom: '0.5px solid',
					borderColor: 'border.main',
					backgroundColor: 'background.paper',
					flexShrink: 0,
				}}
			>
				<Box
					component='button'
					onClick={onClose}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '4px',
						fontSize: '12px',
						fontWeight: 600,
						color: 'status.blue.text',
						background: 'none',
						border: '1px solid',
						borderColor: 'status.blue.border',
						cursor: 'pointer',
						fontFamily: 'inherit',
						padding: '4px 10px',
						borderRadius: '4px',
						'&:hover': { backgroundColor: 'background.subtle' },
					}}
				>
					← Back
				</Box>
				<Avatar
					sx={{
						width: 36,
						height: 36,
						fontSize: '12px',
						fontWeight: 600,
						bgcolor: 'status.blue.bg',
						color: 'status.blue.text',
					}}
				>
					{lp.initials}
				</Avatar>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
						{lp.name}
					</Typography>
					<Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
						{lp.type} · {lp.tier} · Coverage: J. Morales
					</Typography>
				</Box>
				<StatusPill label={`Re-up: ${lp.reup}`} color={reupColor} />
				<StatusPill label={`Health: ${lp.health}`} color={healthColor} />
				{lp.vdrStatus && (
					<StatusPill label={`VDR: ${lp.vdrStatus}`} color='vdr' clickable />
				)}
			</Box>

			{/* scrollable body */}
			<Box
				sx={{
					flex: 1,
					overflowY: 'auto',
					padding: '16px 20px',
					display: 'flex',
					flexDirection: 'column',
					gap: '14px',
				}}
			>
				{/* kpi strip — 4 cards */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: 'repeat(4, 1fr)',
						gap: '10px',
					}}
				>
					<KPICard label='Fund V target' value={lp.commit} />
					<KPICard
						label='Fund V committed'
						value={lp.committed ?? lp.commit}
						sub='Fully called'
						subColor='up'
					/>
					<KPICard label='Last contact' value={lp.lastContact} />
					<KPICard
						label='↔ VDR engagement'
						value={hasSessions ? '3 sessions' : 'No sessions'}
						sub={`${hasSessions ? '38 min' : 'No access'} - open Raise VDR →`}
						vdr
					/>
				</Box>

				{/* two-column: profile left, signals right */}
				<Box
					sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}
				>
					{/* left: LP profile */}
					<Box
						sx={{
							backgroundColor: 'background.paper',
							border: '0.5px solid',
							borderColor: 'border.light',
							borderRadius: '12px',
							padding: '14px',
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								mx: '-14px',
								px: '14px',
								pb: '8px',
								borderBottom: '0.5px solid',
								borderColor: 'border.light',
							}}
						>
							<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
								LP profile
							</Typography>
							<StatusPill
								label={`Tier ${lp.tier?.replace('Tier ', '') ?? '1'}`}
								color='teal'
							/>
						</Box>
						{[
							{ label: 'Investor type', value: lp.type },
							{ label: 'AUM', value: lp.aum },
							{
								label: 'DPI sensitivity',
								value: lp.dpiSensitivity ?? 'Moderate',
							},
							{
								label: 'Co-invest appetite',
								value: lp.coInvestAppetite ?? 'High',
							},
							{
								label: 'Side letter',
								value: lp.sideLetter ?? 'MFN · Co-invest rights',
							},
						].map((row) => (
							<Box
								key={row.label}
								sx={{ display: 'flex', justifyContent: 'space-between' }}
							>
								<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
									{row.label}
								</Typography>
								<Typography
									sx={{
										fontSize: '12px',
										fontWeight: 500,
										textAlign: 'right',
										maxWidth: '55%',
									}}
								>
									{row.value}
								</Typography>
							</Box>
						))}
					</Box>

					{/* right: AIMe signals */}
					<Box
						sx={{
							backgroundColor: 'background.paper',
							border: '0.5px solid',
							borderColor: 'border.light',
							borderRadius: '12px',
							padding: '14px',
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								mx: '-14px',
								px: '14px',
								pb: '8px',
								borderBottom: '0.5px solid',
								borderColor: 'border.light',
							}}
						>
							<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
								AIMe signals
							</Typography>
							<Box
								component='span'
								sx={{
									fontSize: '10px',
									fontWeight: 600,
									color: 'vdr.text',
									backgroundColor: 'vdr.bg',
									border: '0.5px solid',
									borderColor: 'vdr.border',
									borderRadius: '4px',
									padding: '2px 7px',
									cursor: 'pointer',
									'&:hover': { backgroundColor: 'vdr.hover' },
								}}
							>
								↔ VDR inputs
							</Box>
						</Box>
						{[
							{
								label: 'Re-up probability',
								pill: { label: lp.reup, color: reupColor },
								sub: 'Prior commitment · VDR engagement 3 sessions · PPM access ×3',
							},
							{
								label: 'Relationship health',
								pill: { label: lp.health, color: healthColor },
								sub: `Last contact ${lp.lastContact} · ${lp.openItems} overdue item${lp.openItems !== 1 ? 's' : ''}`,
							},
							{
								label: 'VDR raise status',
								pill: { label: lp.vdrStatus, color: 'vdr' },
							},
						].map((row, i) => (
							<Box
								key={i}
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'flex-start',
									py: '2px',
								}}
							>
								<Box sx={{ flex: 1, pr: 1 }}>
									<Typography
										sx={{ fontSize: '12px', color: 'text.secondary' }}
									>
										{row.label}
									</Typography>
									{row.sub && (
										<Typography
											sx={{
												fontSize: '11px',
												color: 'text.secondary',
												mt: '1px',
											}}
										>
											{row.sub}
										</Typography>
									)}
								</Box>
								{row.pill && (
									<StatusPill label={row.pill.label} color={row.pill.color} />
								)}
							</Box>
						))}
						{/* next action */}
						<Box
							sx={{
								mt: '4px',
								backgroundColor: 'background.subtle',
								borderRadius: '6px',
								padding: '8px 10px',
							}}
						>
							<Typography sx={{ fontSize: '11px', color: 'text.primary' }}>
								{nextAction}
							</Typography>
						</Box>
						{/* draft button */}
						<Box
							component='button'
							sx={{
								alignSelf: 'flex-start',
								fontSize: '11px',
								fontWeight: 600,
								color: 'vdr.text',
								background: 'vdr.bg',
								border: '0.5px solid',
								borderColor: 'vdr.border',
								borderRadius: '5px',
								padding: '3px 8px',
								cursor: 'pointer',
								fontFamily: 'inherit',
								'&:hover': { backgroundColor: 'vdr.hover' },
							}}
						>
							Draft outreach from VDR signal
						</Box>
					</Box>
				</Box>

				{/* interaction history — full width */}
				<Box
					sx={{
						backgroundColor: 'background.paper',
						border: '0.5px solid',
						borderColor: 'border.light',
						borderRadius: '12px',
						overflow: 'hidden',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							padding: '10px 14px',
							borderBottom: '0.5px solid',
							borderColor: 'border.light',
						}}
					>
						<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
							Interaction History
						</Typography>
						<Box
							component='span'
							sx={{
								fontSize: '10px',
								fontWeight: 600,
								color: 'vdr.text',
								backgroundColor: 'vdr.bg',
								border: '0.5px solid',
								borderColor: 'vdr.border',
								borderRadius: '4px',
								padding: '2px 7px',
								cursor: 'pointer',
								'&:hover': { backgroundColor: 'vdr.hover' },
							}}
						>
							↔ VDR events auto-logged · view full activity in Raise VDR
						</Box>
					</Box>
					{fakeData.lpInteractionHistory.map((item, i) => (
						<Box
							key={i}
							sx={{
								display: 'flex',
								gap: '10px',
								padding: '8px 14px',
								borderBottom:
									i < fakeData.lpInteractionHistory.length - 1
										? '0.5px solid'
										: 'none',
								borderColor: 'border.light',
								backgroundColor: item.vdr ? 'vdr.bg' : 'transparent',
							}}
						>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
									<Typography
										sx={{
											fontSize: '12px',
											fontWeight: 500,
											color: 'text.primary',
										}}
									>
										{item.text}
									</Typography>
									<Typography
										sx={{
											fontSize: '11px',
											color: 'text.secondary',
											whiteSpace: 'nowrap',
											ml: 1,
										}}
									>
										{item.date}
									</Typography>
								</Box>
								{item.note && (
									<Typography
										sx={{
											fontSize: '11px',
											color: 'text.secondary',
											mt: '1px',
										}}
									>
										{item.note}
									</Typography>
								)}
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
};

LPPanel.propTypes = {
	open: PropTypes.bool.isRequired,
	lp: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	onOpenDrawer: PropTypes.func.isRequired,
};

export default LPPanel;
